import { getUserProducts } from '../utils/userProductStorage';
import { itemDisplayValue } from '../lib/itemValuation';
import React, { useMemo, useState, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../styles/ThemeProvider';
import { useCommonStyles } from '../styles/useCommonStyles';
import { supabase } from '../lib/supabase';
import { getVerifiedPortfolioValue } from '../lib/portfolioValue';
import PortfolioChart from './PortfolioChart';

interface PortfolioSnapshot {
  snapshot_date: string;
  total_value: number;
  item_count: number;
}

interface Props {
  userId: string;
  onSharePress?: () => void;
  // Bumped by parent to trigger a re-fetch (e.g., from pull-to-refresh).
  refreshKey?: number;
  // Invalidate independently of the pending-work callback, including its final read.
  valuationRevision?: readonly { id: string }[];
  active?: boolean;
}

export default function PortfolioHeader({ userId, onSharePress, refreshKey, valuationRevision, active = true }: Props) {
  const theme = useTheme();
  const commonStyles = useCommonStyles();

  const [loadError, setLoadError] = useState(false);
  const [totalValue, setTotalValue] = useState<number>(0);
  const [itemCount, setItemCount] = useState<number>(0);
  const [unvaluedCount, setUnvaluedCount] = useState<number>(0);
  const [historyUnavailable, setHistoryUnavailable] = useState(false);
  const [portfolioHistory, setPortfolioHistory] = useState<PortfolioSnapshot[]>([]);
  const [retryKey, setRetryKey] = useState(0);
  const requestKey = useMemo(() => ({ userId, refreshKey, valuationRevision, active, retryKey }), [userId, refreshKey, valuationRevision, active, retryKey]);
  const [loadedKey, setLoadedKey] = useState<typeof requestKey | null>(null);
  const current = loadedKey === requestKey;

  const styles = useMemo(
    () =>
      StyleSheet.create({
        container: {
          backgroundColor: theme.colors.backgroundPrimary,
        },
        header: {
          paddingTop: theme.spacing.xl,
          paddingHorizontal: theme.spacing.xl,
          paddingBottom: theme.spacing.xs,
          backgroundColor: theme.colors.backgroundPrimary,
        },
        headerRow: {
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
        },
        shareButton: {
          padding: theme.spacing.sm,
        },
        chartWrapper: {
          overflow: 'hidden',
        },
        summaryLine: {
          paddingHorizontal: theme.spacing.xl,
          paddingBottom: theme.spacing.md,
        },
        summaryText: {
          ...theme.typography.sm,
          color: theme.colors.textSecondary,
        },
      }),
    [theme],
  );

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    const load = async () => {
      try {
        const [value, products] = await Promise.all([getVerifiedPortfolioValue(userId), getUserProducts(userId)]);
        if (cancelled) return;
        if (!value) throw new Error('Portfolio unavailable');
        setLoadError(false);
        setHistoryUnavailable(Boolean(value.usesRestorationValuations));
        setTotalValue(value.totalValue);
        setItemCount(value.itemCount);
        setUnvaluedCount(products.filter(item => ['owned', 'active'].includes(item.status) && itemDisplayValue(item) === null).length);
        setPortfolioHistory([]);
        setLoadedKey(requestKey);
        if (value.usesRestorationValuations) return;
        const { data, error } = await supabase.from('portfolio_snapshots')
          .select('snapshot_date, total_value, item_count').eq('user_id', userId)
          .gte('snapshot_date', new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0])
          .order('snapshot_date', { ascending: true });
        if (!cancelled && !error && data) setPortfolioHistory(data);
      } catch (error) {
        if (!cancelled) { setLoadError(true); setLoadedKey(requestKey); }
        console.error('PortfolioHeader: Error fetching data:', error);
      }
    };
    void load();
    return () => { cancelled = true; };
  }, [requestKey, active, userId]);

  return (
    <View style={styles.container}>
      {current && loadError && <>
        <Text accessibilityRole="alert">Unable to refresh valuations</Text>
        <TouchableOpacity accessibilityRole="button" onPress={() => setRetryKey(key => key + 1)}><Text>Retry portfolio value</Text></TouchableOpacity>
      </>}
      {/* Header with total value */}
      <View style={styles.header}>
        <View style={styles.headerRow}>
          <Text style={commonStyles.valuePrimary}>
            {!current ? 'Refreshing portfolio value…' : loadError ? 'Portfolio value unavailable' : `$${totalValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          </Text>
          <TouchableOpacity
            style={styles.shareButton}
            onPress={onSharePress}
          >
            <Ionicons name="share-outline" size={22} color={theme.colors.textPrimary} />
          </TouchableOpacity>
        </View>
      </View>

      {/* Portfolio Chart */}
      {current && !loadError && <View style={styles.chartWrapper}>
        {historyUnavailable ? <Text>Qualified portfolio history is unavailable</Text> : <PortfolioChart
          data={portfolioHistory}
          days={30}
          totalValue={totalValue || 0}
        />}
      </View>}

      {/* Summary line — item count only (change % is already shown by PortfolioChart) */}
      {current && !loadError && <View style={styles.summaryLine}>
        <Text style={styles.summaryText}>
          {itemCount} {historyUnavailable ? 'valued ' : ''}item{itemCount !== 1 ? 's' : ''}
          {unvaluedCount > 0 ? ` · ${unvaluedCount} not yet valued` : ''}
        </Text>
      </View>}
    </View>
  );
}
